import * as ActionTypes from "@/stores/sale/actions"
import _ from "lodash"

/**
 * WebApi
 */
class WebApi {
  /**
   * 提供“未实现”函数的默认实现
   */
  notImpl(funcName) {
    return {
      ajax: () => {
        return Promise.reject(`'${funcName}' is not implemented`)
      },
      url: `#the-url-to-${funcName}`,
    }
  }

  /**
   * 获取登录态
   * @returns {{ajax, url}}
   */
  getLoginStatus() {
    return this.notImpl("getLoginStatus")
  }

  /**
   * 注销
   * @return {{ajax, url}}
   */
  logout() {
    return this.notImpl("logout")
  }

  /**
   * 获取 JWT token
   * @returns {{ajax, url}}
   */
  getJwtToken(type) {
    return this.notImpl("getJwtToken")
  }

  /**
   * 获取用户信息
   * @param userId
   * @return {{ajax: (function(): Promise<never>), url: string}}
   */
  getUser(userId) {
    return this.notImpl("getUser")
  }

  getUserIfNeed(userId) {
    return this.getUser(userId).when((state) => {
      const user = state.userRepo[userId]
      return !user || !user.query
    })
  }

  /**
   * 获取地域列表
   * @return {{ajax, url}}
   */
  getAreaList() {
    return this.notImpl("getAreaList")
  }

  /**
   * 获取行业列表
   * @return {{ajax, url}}
   */
  getIndustryList() {
    return this.notImpl("getIndustryList")
  }

  /**
   * 获取推广目标列表
   * @return {{ajax, url}}
   */
  getPromotedObjectList() {
    return this.notImpl("getPromotedObjectList")
  }

  /**
   * 获取线索来源列表
   * @return {{ajax, url}}
   */
  getSourceTypeList() {
    return this.notImpl("getPromotedObjectList")
  }

  /**
   * 获取线索标签配置
   * @return {{ajax, url}}
   */
  getSourceTypeTag() {
    return this.notImpl("getSourceTypeTag")
  }

  /**
   * 获取服务商信息
   * @param agencyId
   * @param needAdditionalInfo
   * @return {{ajax, url}}
   */
  getAgency(agencyId, needAdditionalInfo) {
    return this.notImpl("getAgency")
  }

  /**
   * 获取服务商信息
   * @return {{ajax, url}}
   */
  getAgencyList(needAdditionalInfo) {
    return this.notImpl("getAgencyList")
  }

  /**
   * 关联服务商
   * @return {{ajax, url}}
   */
  bindAgency() {
    return this.notImpl("bindAgency")
  }

  /**
   * 校验代理商 ID 有效性
   * @return {{ajax, url}}
   */
  checkAgency() {
    return this.notImpl("checkAgency")
  }

  /**
   * 切换服务商
   * @param agencyId
   * @return {{ajax, url}}
   */
  switchAgency(agencyId) {
    return this.notImpl("switchAgency")
  }

  /**
   * 获取架构树
   * @return {{ajax, url}}
   */
  getOrganizationTree() {
    return this.notImpl("getOrganizationTree")
  }
  getOrganizationTreeIfNeed() {
    return this.getOrganizationTree().when((state) => !state.organizationTree.query)
  }

  /**
   * 获取用户所属团队列表
   */
  getEmployeeWorkingTeams(employeeId) {
    return this.notImpl("getEmployeeWorkingTeams")
  }

  /**
   * 获取权限列表
   * @return {{ajax, url}}
   */
  getPrivilegeList() {
    return this.notImpl("getPrivilegeList")
  }

  getPrivilegeListIfNeed() {
    return this.getPrivilegeList().when((state) => !state.privilegeMap.query)
  }

  getEmployeePermission(employeeId) {
    return this.notImpl("getEmployeePermission")
  }

  /**
   * 获取用户列表
   * @return {{ajax, url}}
   */
  getEmployeeUserList() {
    return this.notImpl("getEmployeeUserList")
  }

  getEmployeeUserListIfNeed() {
    return this.getEmployeeUserList().when((state) => !state.employeeList.query)
  }

  /**
   * 查询用户全信息列表：异步拉取坐席信息、坐席技能列表
   *
   */
  queryFullEmployeeUserList(data = { keywords: "" }, onSuccess, onError) {
    this.getEmployeeUserListIfNeed()
      .dispatch(ActionTypes.FETCH_EMPLOYEE_USER_LIST, {
        data: data,
      })
      .then(
        (data) => {
          if (onSuccess) {
            onSuccess(data.list)
          }

          // 拉取坐席技能列表
          _.forEach(_.uniq(_.map(data.list, (e) => e.callCenterId)), (id) => {
            if (!!id) {
              this.getSkillGroupsIfNeed(id).dispatch(ActionTypes.FETCH_SEAT_SKILL_GROUP_LIST.bind({ callCenterId: id }))
            }
          })

          // 拉取坐席信息
          _.forEach(data.list, (employee) => {
            let { employeeId, seatNo, callCenterId } = employee
            if (seatNo && callCenterId) {
              this.getEmployeeSeatInfo(employeeId).dispatch(ActionTypes.FETCH_EMPLOYEE_SEAT_INFO.bind({ employeeId }))
            }
          })
        },
        (error) => {
          if (onError) {
            onError(error)
          }
        }
      )
  }

  /**
   * 查询用户全信息列表：不拉取坐席信息、坐席技能列表
   */
  queryPureFullEmployeeUserList(data = { keywords: "" }, onSuccess, onError) {
    this.getEmployeeUserListIfNeed()
      .dispatch(ActionTypes.FETCH_FULL_EMPLOYEE_USER_LIST, {
        data,
      })
      .then(
        (data) => {
          if (onSuccess) {
            onSuccess(data.list)
          }
        },
        (error) => {
          if (onError) {
            onError(error)
          }
        }
      )
  }

  /**
   * 新增或修改代理商首选项数据
   * @return {{ajax, url}}
   */
  editAgencyPreference() {
    return this.notImpl("editAgencyPreference")
  }

  /**
   * 拉取用户坐席信息
   * @return {{ajax, url}}
   */
  getEmployeeSeatInfo(employeeId) {
    return this.notImpl("getEmployeeSeatInfo")
  }

  /**
   * 搜索线索
   * @return {{ajax, url}}
   */
  searchLead() {
    return this.notImpl("searchLead")
  }

  /**
   * 获取当前员工
   * @return {{ajax, url}}
   */
  getCurrentEmployeeUser() {
    return this.notImpl("getCurrentEmployeeUser")
  }

  /**
   * 创建线索
   * @return {{ajax, url}}
   */
  createLead() {
    return this.notImpl("createLead")
  }

  /**
   * 清洗线索
   * @return {{ajax, url}}
   */
  cleanLead() {
    return this.notImpl("cleanLead")
  }

  /**
   * 获取线索
   * @param leadId
   * @return {{ajax, url}}
   */
  getLead(leadId) {
    return this.notImpl("getLead")
  }

  /**
   * 分配线索
   * @return {{ajax, url}}
   */
  assignLeads() {
    return this.notImpl("assignLeads")
  }

  /**
   * 转移外勤单
   * @return {{ajax, url}}
   */
  outsideTransfer() {
    return this.notImpl("outsideTransfer")
  }

  /**
   * 转移开户单
   * @return {{ajax, url}}
   */
  registrationTransfer() {
    return this.notImpl("registrationTransfer")
  }

  /**
   * 转为商机
   * @return {{ajax, url}}
   */
  toPotential() {
    return this.notImpl("toPotential")
  }

  /**
   * 搜索商机
   * @return {{ajax, url}}
   */
  searchPotential() {
    return this.notImpl("searchPotential")
  }

  /**
   * 通过关键字（leadId、companyName）搜索商机
   * @return {{ajax, url}}
   */
  searchPotentialByKeyword() {
    return this.notImpl("searchPotentialByKeyword")
  }

  /**
   * 获取商机
   * @param leadId
   * @return {{ajax, url}}
   */
  getPotential(leadId) {
    return this.notImpl("getPotential")
  }

  /**
   * 更新商机
   * @param leadId
   * @return {{ajax, url}}
   */
  updatePotential(leadId) {
    return this.notImpl("updatePotential")
  }

  /**
   * 分配商机
   * @return {{ajax, url}}
   */
  assignPotentials() {
    return this.notImpl("assignPotentials")
  }

  /**
   * 释放商机到公海池
   * @return {{ajax, url}}
   */
  releasePotentials() {
    return this.notImpl("releasePotentials")
  }

  /**
   * 获取订单
   * @return {{ajax, url}}
   */
  getOrder(leadId) {
    return this.notImpl("getOrder")
  }

  /**
   * 获取外勤单列表
   * @return {{ajax, url}}
   */
  getOutsideList() {
    return this.notImpl("getOutsideList")
  }

  /**
   * 获取外勤单列表的数量
   * @return {{ajax, url}}
   */
  getOutsideCount() {
    return this.notImpl("getOutsideCount")
  }

  /**
   * 下载外勤单列表
   * @return {{ajax, url}}
   */
  downloadOutsideExcel() {
    return this.notImpl("downloadOutsideExcel")
  }

  /**
   * 新建外勤单
   * @return {{ajax, url}}
   */
  createOutsideTicket() {
    return this.notImpl("createOutsideTicket")
  }

  /**
   * 更新外勤单
   * @return {{ajax, url}}
   */
  updateOutsideTicket(outsideTicketId) {
    return this.notImpl("updateOutsideTicket")
  }

  /**
   * 获取外勤单详情
   * @return {{ajax, url}}
   */
  getOutsideTicket(outsideTicketId) {
    return this.notImpl("getOutsideTicket")
  }
  /**
   * is 审核
   * @return {{ajax, url}}
   */
  insideReview() {
    return this.notImpl("insideReview")
  }
  /**
   * 回填
   * @return {{ajax, url}}
   */
  backFill() {
    return this.notImpl("backFill")
  }
  /**
   * 撤回
   * @return {{ajax, url}}
   */
  outsideWithdraw() {
    return this.notImpl("backFill")
  }
  /**
   * 关闭
   * @return {{ajax, url}}
   */
  outsideClose() {
    return this.notImpl("backFill")
  }
  /**
   * 是否接受外勤单
   * @return {{ajax, url}}
   */
  outsideAccept() {
    return this.notImpl("outsideAccept")
  }
  /**
   * os 审核
   * @return {{ajax, url}}
   */
  outsideReview() {
    return this.notImpl("outsideReview")
  }
  /**
   * 获取外勤联系人
   * @return {{ajax, url}}
   */
  getOutsideContactList(outsideTicketId) {
    return this.notImpl("getOutsideContactList")
  }
  /**
   * 获取外勤记录
   * @return {{ajax, url}}
   */
  getOutsideTicketRecordList(outsideTicketId) {
    return this.notImpl("getOutsideTicketRecordList")
  }
  /**
   * 更新订单中线索信息
   * @return {{ajax, url}}
   */
  updateOrderLeadInfo(leadId) {
    return this.notImpl("updateOrderLeadInfo")
  }
  /**
   * 编辑订单
   * @return {{ajax, url}}
   */
  updateOrder(leadId) {
    return this.notImpl("updateOrder")
  }

  /**
   * 公海申领
   * @return {{ajax, url}}
   */
  obtainPotentials() {
    return this.notImpl("obtainPotentials")
  }

  /**
   * 搜索商机
   * @return {{ajax, url}}
   */
  searchOrder() {
    return this.notImpl("searchOrder")
  }

  /**
   * 新增广点通广告主
   * @return {{ajax, url}}
   */
  addAdvertisingAccount() {
    return this.notImpl("addAdvertisingAccount")
  }

  /**
   * 更新广点通广告主
   * @return {{ajax, url}}
   */
  updateAdvertisingAccount() {
    return this.notImpl("updateAdvertisingAccount")
  }

  /**
   * 开户单详情
   * @return {{ajax, url}}
   */
  getAdvertisingDetail(registrationTicketId) {
    return this.notImpl("getAdvertisingDetail")
  }

  /**
   * 获取广告主行业资质体系结构
   * @return {{ajax, url}}
   */
  getIndustryInfo(industryId) {
    return this.notImpl("getIndustryInfo")
  }

  /**
   * 上传图片
   * @return {{ajax, url}}
   */
  uploadImage(request) {
    return this.notImpl("uploadImage")
  }

  /**
   * 获取开户信息
   * @return {{ajax, url}}
   */
  getAdvertiserInfo(uid) {
    return this.notImpl("uid")
  }

  /**
   * 撤回
   * @return {{ajax, url}}
   */
  registrationWithdraw() {
    return this.notImpl("registrationWithdraw")
  }
  /**
   * 关闭
   * @return {{ajax, url}}
   */
  registrationClose() {
    return this.notImpl("registrationClose")
  }

  /**
   * 获取客户信息
   * @param customerId
   * @return {{ajax: (function(): Promise<never>), url: string}}
   */
  getCustomer(customerId) {
    return this.notImpl("getCustomer")
  }

  /**
   * 检查客户信息
   * @return {{ajax: (function(): Promise<never>), url: string}}
   */
  checkCustomer() {
    return this.notImpl("checkCustomer")
  }

  /**
   * 获取联系人列表
   * @param leadId
   * @return {{ajax, url}}
   */
  getContactList(leadId) {
    return this.notImpl("getContactList")
  }

  /**
   * 获取跟进记录列表
   * @param leadId
   * @return {{ajax, url}}
   */
  getActionRecordList(leadId) {
    return this.notImpl("getActionRecordList")
  }

  /**
   * 添加跟进记录
   * @param leadId
   * @return {{ajax, url}}
   */
  addActionRecord(leadId) {
    return this.notImpl("addActionRecord")
  }

  /**
   * 更新跟进记录
   * @param leadId
   * @param recordId
   * @return {{ajax, url}}
   */
  updateActionRecord(leadId, recordId) {
    return this.notImpl("updateActionRecord")
  }

  /**
   * 呼出电话号码
   * @return {{ajax: (function(): Promise<never>), url: string}}
   */
  dialOutCheck() {
    return this.notImpl("dialOutCheck")
  }

  /**
   * 呼出电话号码
   * @return {{ajax: (function(): Promise<never>), url: string}}
   */
  dialOut() {
    return this.notImpl("dialOut")
  }

  getSeatRoles() {
    return this.notImpl("getSeatRoles")
  }

  getSeatRolesIfNeed() {
    return this.getSeatRoles().when((state) => !state.seatRoles.query)
  }

  getSkillGroups(callCenterId) {
    return this.notImpl("getSeatRoles")
  }

  getSkillGroupsIfNeed(callCenterId) {
    return this.getSkillGroups(callCenterId).when((state) => !state.skillGroups || !state.skillGroups[callCenterId] || !state.skillGroups[callCenterId].query)
  }

  /**
   * 跟进线索/商机
   * @returns {{ajax, url}}
   */
  traceCalling() {
    return this.notImpl("traceCalling")
  }

  fetchStatusBar() {
    return this.notImpl("fetchStatusBar")
  }

  /**
   * 查询批量线索导入的结果
   * @returns {{ajax, url}}
   */
  queryImportLeadResult() {
    return this.notImpl("queryImportLeadResult")
  }

  getLeadCustomizedViewFields(employeeId) {
    return this.notImpl("getLeadCustomizedViewFields")
  }

  getPotentialCustomizedViewFields(employeeId) {
    return this.notImpl("getPotentialCustomizedViewFields")
  }

  getOrderCustomizedViewFields(employeeId) {
    return this.notImpl("getOrderCustomizedViewFields")
  }

  saveLeadCustomizedViewFields(employeeId) {
    return this.notImpl("saveLeadCustomizedViewFields")
  }

  savePotentialCustomizedViewFields(employeeId) {
    return this.notImpl("savePotentialCustomizedViewFields")
  }

  saveOrderCustomizedViewFields(employeeId) {
    return this.notImpl("saveOrderCustomizedViewFields")
  }

  getConversionCustomizedViewFields(employeeId) {
    return this.notImpl("getConversionCustomizedViewFields")
  }

  getConversionRateCustomizedViewFields(employeeId) {
    return this.notImpl("getConversionRateCustomizedViewFields")
  }

  getEfficiencyReportCustomizedViewFields(employeeId) {
    return this.notImpl("getEfficiencyReportCustomizedViewFields")
  }

  saveConversionReportCustomizedViewFields(employeeId) {
    return this.notImpl("saveConversionReportCustomizedViewFields")
  }

  saveConversionRateReportCustomizedViewFields(employeeId) {
    return this.notImpl("saveConversionRateReportCustomizedViewFields")
  }

  saveEfficiencyReportCustomizedViewFields(employeeId) {
    return this.notImpl("saveEfficiencyReportCustomizedViewFields")
  }

  downloadLeadExcel() {
    return this.notImpl("downloadLeadExcel")
  }

  downloadPotentialExcel() {
    return this.notImpl("downloadPotentialExcel")
  }

  downloadOrderExcel() {
    return this.notImpl("downloadOrderExcel")
  }

  downloadUserExcel() {
    return this.notImpl("downloadUserExcel")
  }

  downloadAllUserExcel() {
    return this.notImpl("downloadAllUserExcel")
  }

  downloadConversionReport() {
    return this.notImpl("downloadConversionReport")
  }

  downloadConversionRateReport() {
    return this.notImpl("downloadConversionRateReport")
  }

  downloadReportCost() {
    return this.notImpl("downloadReportCost")
  }

  downloadRechargeReport() {
    return this.notImpl("downloadRechargeReport")
  }

  queryDownloadResult(uuid) {
    return this.notImpl("queryDownloadResult")
  }

  getBlackListRecords() {
    return this.notImpl("getBlackListRecords")
  }

  addBlackListRecord() {
    return this.notImpl("addBlackListRecord")
  }

  releaseBlackListRecord() {
    return this.notImpl("releaseBlackListRecord")
  }

  batchReleaseBlackListRecord() {
    return this.notImpl("batchReleaseBlackListRecord")
  }

  checkBlackListRecord() {
    return this.notImpl("checkBlackListRecord")
  }

  searchFeature() {
    return this.notImpl("searchFeature")
  }

  saveFeature() {
    return this.notImpl("saveFeature")
  }

  searchReportTrafficDetail() {
    return this.notImpl("searchReportTrafficDetail")
  }

  searchReportTrafficDaily() {
    return this.notImpl("searchReportTrafficDaily")
  }

  getTeamCallData() {
    return this.notImpl("getReportSaleData")
  }

  getTeamSalesData() {
    return this.notImpl("getReportSaleData")
  }

  searchReportCost() {
    return this.notImpl("searchReportCost")
  }

  /**
   * 新增 IS 审核人
   * @returns {{ajax: (function(): Promise<never>), url: string}}
   */
  addInsideReviewer() {
    return this.notImpl("addInsideReviewer")
  }

  /**
   * 获取 IS 审核人
   * @returns {{ajax: (function(): Promise<never>), url: string}}
   */
  getInsideReviewers() {
    return this.notImpl("getInsideReviewers")
  }

  /**
   * 删除 IS 审核人
   * @returns {{ajax: (function(): Promise<never>), url: string}}
   */
  deleteInsideReviewer() {
    return this.notImpl("deleteInsideReviewer")
  }
  /**
   * 新增 OS 审核人
   * @returns {{ajax: (function(): Promise<never>), url: string}}
   */
  addOutsideReviewer() {
    return this.notImpl("addOutsideReviewer")
  }

  /**
   * 获取 OS 审核人
   * @returns {{ajax: (function(): Promise<never>), url: string}}
   */
  getOutsideReviewers() {
    return this.notImpl("getOutsideReviewers")
  }

  /**
   * 删除 OS 审核人
   * @returns {{ajax: (function(): Promise<never>), url: string}}
   */
  deleteOutsideReviewer() {
    return this.notImpl("deleteOutsideReviewer")
  }

  /**
   * 查询业绩分析列表
   * @returns {{ajax: (function(): Promise<never>), url: string}}
   */
  getPerformanceList() {
    return this.notImpl("getPerformanceList")
  }
  /**
   * 下载业绩分析列表
   * @returns {{ajax: (function(): Promise<never>), url: string}}
   */
  downloadPerformanceReport() {
    return this.notImpl("downloadPerformanceReport")
  }
  /**
   * 删除线索
   * @return {{ajax, url}}
   */
  deleteLead(leadId) {
    return this.notImpl("deleteLead")
  }
  /**
   * 删除商机
   * @return {{ajax, url}}
   */
  deletePotential(potentialId) {
    return this.notImpl("deletePotential")
  }
  /**
   * 删除订单
   * @return {{ajax, url}}
   */
  deleteOrder(orderId) {
    return this.notImpl("deleteOrder")
  }

  /**
   * 获取下单产品列表
   * @returns {{ajax: (function(): Promise<never>), url: string}}
   */
  getOrderingProductList() {
    return this.notImpl("getOrderingProductList")
  }
  /**
   * 获取业务分析转化量列表
   * @return {{ajax, url}}
   */
  searchConversion() {
    return this.notImpl("searchConversion")
  }
  /**
   * 获取业务分析转化量列表
   * @return {{ajax, url}}
   */
  searchConversionRate() {
    return this.notImpl("searchConversionRate")
  }
  /**
   * 获取效能评估列表
   * @returns {{ajax, url}}
   */
  searchEfficiencyReport() {
    return this.notImpl("searchEfficiencyReport")
  }
  /**
   * 下载效能评估列表
   * @returns {{ajax, url}}
   */
  downloadEfficiencyReport() {
    return this.notImpl("downloadEfficiencyReport")
  }

  /**
   * 获取录音 url
   */
  getRecord() {
    return this.notImpl("getRecord")
  }
}

export default WebApi
